import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Truck, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { REGIONS } from "@/lib/countries";
import api from "@/lib/api";

const emptyForm = { name: "", description: "", price: "0", delivery_time: "", free_above: "", active: true, rates: {} };
const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export function ShippingAdmin() {
  const { t } = useApp();
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);

  const load = async () => {
    try { const r = await api.get("/admin/shop/shipping"); setOptions(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  useEffect(() => { if (!loaded.current) { loaded.current = true; load(); } }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    const rates = {};
    Object.entries(o.rates || {}).forEach(([k, v]) => { rates[k] = String(v); });
    setForm({ name: o.name, description: o.description || "", price: String(o.price ?? 0), delivery_time: o.delivery_time || "", free_above: o.free_above != null ? String(o.free_above) : "", active: !!o.active, rates });
    setOpen(true);
  };

  const setRate = (code, val) => setForm((f) => ({ ...f, rates: { ...f.rates, [code]: val } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("shippingName")); return; }
    setBusy(true);
    try {
      const rates = {};
      Object.entries(form.rates || {}).forEach(([k, v]) => {
        if (String(v).trim() !== "" && !Number.isNaN(parseFloat(v))) rates[k] = parseFloat(v);
      });
      const payload = {
        name: form.name.trim(), description: form.description.trim(),
        price: parseFloat(form.price) || 0, delivery_time: form.delivery_time.trim(),
        free_above: form.free_above.trim() === "" ? null : (parseFloat(form.free_above) || 0),
        rates,
        active: !!form.active,
      };
      if (editing) await api.put(`/admin/shop/shipping/${editing.id}`, payload);
      else await api.post("/admin/shop/shipping", payload);
      toast.success(t("saved"));
      setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(t("deleteShippingConfirm"))) return;
    await api.delete(`/admin/shop/shipping/${o.id}`);
    toast.success(t("saved")); await load();
  };

  return (
    <div className="mt-6" data-testid="shipping-admin">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Truck size={18} weight="bold" />{t("shippingAdminTitle")}</h3>
        <button data-testid="new-shipping-btn" onClick={openNew} className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1" style={{ background: "var(--app-primary)" }}><Plus size={16} weight="bold" />{t("newShipping")}</button>
      </div>
      <div className="space-y-2 mt-3" data-testid="shipping-list">
        {options.length === 0 && <p className="font-body text-sm text-gray-500">{t("noShipping")}</p>}
        {options.map((o) => (
          <div key={o.id} data-testid={`shipping-row-${o.id}`} className="border-2 border-gray-800 rounded-2xl p-3 bg-white flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-heading font-semibold text-gray-900 truncate">{o.name}</span>
                {!o.active && <span className="text-[10px] font-heading font-semibold px-2 py-0.5 rounded-full text-white bg-gray-400">inaktiv</span>}
              </div>
              <p className="font-body text-xs text-gray-500">{money(o.price)}{o.delivery_time ? ` · ${o.delivery_time}` : ""}{o.free_above != null ? ` · ${t("freeLabel")} ${t("priceFrom")} ${money(o.free_above)}` : ""}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`edit-shipping-${o.id}`} onClick={() => openEdit(o)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><PencilSimple size={14} weight="bold" />{t("edit")}</button>
              <button data-testid={`delete-shipping-${o.id}`} onClick={() => remove(o)} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1 text-red-600"><Trash size={14} weight="bold" />{t("delete")}</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shipping-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("editShippingTitle") : t("newShipping")}</DialogTitle>
            <DialogDescription>{t("shippingAdminTitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("shippingName")}</label>
              <input data-testid="shipping-name-input" className="sb-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("shippingDesc")}</label>
              <input data-testid="shipping-desc-input" className="sb-input mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("shippingBasePrice")}</label>
                <input data-testid="shipping-price-input" type="number" min="0" step="0.01" className="sb-input mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("shippingFreeAbove")}</label>
                <input data-testid="shipping-free-above-input" type="number" min="0" step="0.01" className="sb-input mt-1" value={form.free_above} onChange={(e) => setForm({ ...form, free_above: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("shippingDeliveryTime")}</label>
              <input data-testid="shipping-delivery-input" className="sb-input mt-1" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} />
            </div>
            <div className="border-2 border-gray-300 rounded-2xl p-3">
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("countryRates")}</label>
              <p className="font-body text-[11px] text-gray-400 mb-2">{t("countryRatesHint")}</p>
              <div className="space-y-2">
                {REGIONS.map((r) => (
                  <div key={r.code} className="flex items-center gap-2">
                    <span className="font-body text-sm text-gray-700 flex-1">{r.label}</span>
                    <input data-testid={`shipping-rate-${r.code}`} type="number" min="0" step="0.01" placeholder="€" className="sb-input !py-1.5 text-sm w-28" value={form.rates?.[r.code] ?? ""} onChange={(e) => setRate(r.code, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between border-2 border-gray-800 rounded-2xl px-3 py-2.5 bg-white">
              <span className="font-body text-sm">{t("shippingActive")}</span>
              <Switch data-testid="shipping-active-toggle" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <button data-testid="save-shipping-btn" onClick={save} disabled={busy} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{t("saveShipping")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
